import type { SupplyKind } from "@frontier/content/supplies";
import { useEffect, useRef } from "react";
import type {
  MissionView,
  PartyMarker,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import type { MissionObjective } from "@frontier/content/contracts";
import type { NavigationState } from "./game/navigation-types";
import { mountGame } from "./game/mount-game";
import type { GameController, InputIntent } from "./game/mount-game";
import { abilityPhysicalSlot } from "./ability-controls";

interface Props {
  world: WorldSnapshot | null;
  worldReceivedAt: number;
  runId: string;
  intro: boolean;
  tutorialGuidanceVisible: boolean;
  mission: MissionView | null;
  eventObjective: MissionObjective | null;
  partyMarker: PartyMarker | null;
  friends: readonly string[];
  suspended: boolean;
  playerId: string;
  boost: boolean;
  targetId: string;
  onInput: (input: InputIntent) => void;
  onTarget: (id: string) => void;
  onSupply?: (kind?: SupplyKind) => void;
  onAbility: (slot: number) => void;
  onInteract: () => void;
  onError: (message: string) => void;
  onReady: (controller: GameController | null) => void;
  onNavigation: (state: NavigationState) => void;
}

export function GameViewport(props: Props) {
  const element = useRef<HTMLDivElement>(null);
  const controller = useRef<GameController | null>(null);
  const latest = useRef(props);
  latest.current = props;
  useEffect(() => {
    const host = element.current;
    if (!host) return;
    let cancelled = false;
    void mountGame(host, {
      onInput: (input) => latest.current.onInput(input),
      onTarget: (id) => latest.current.onTarget(id),
      onSupply: kind => latest.current.onSupply?.(kind),
      onAbility: (index) => {
        const current = latest.current;
        if (current.suspended) return;
        const pilot = current.world?.players.find(player => player.id === current.playerId);
        const slot = abilityPhysicalSlot(pilot?.controls, index);
        if (slot !== null) current.onAbility(slot);
      },
      onInteract: () => latest.current.onInteract(),
      onNavigation: (state) => {
        if (!cancelled) latest.current.onNavigation(state);
      },
    })
      .then((game) => {
        if (cancelled) {
          game.dispose();
          return;
        }
        controller.current = game;
        game.setLocalPlayerId(latest.current.playerId);
        game.setFriends(latest.current.friends);
        game.setSuspended(latest.current.suspended);
        game.setControls({ boost: latest.current.boost });
        game.selectTarget(latest.current.targetId);
        game.setFlightIdentity(latest.current.runId, latest.current.intro);
        game.setTutorialGuidanceVisible(latest.current.tutorialGuidanceVisible);
        game.setMission(latest.current.mission);
        game.setEventObjective(latest.current.eventObjective);
        game.setPartyMarker(latest.current.partyMarker);
        if (latest.current.world)
          game.setSnapshot(
            latest.current.world,
            latest.current.worldReceivedAt,
          );
        latest.current.onReady(game);
      })
      .catch((error: Error) =>
        latest.current.onError(
          `Не удалось запустить графику: ${error.message}`,
        ),
      );
    return () => {
      cancelled = true;
      controller.current?.dispose();
      controller.current = null;
      latest.current.onReady(null);
    };
  }, []);
  useEffect(
    () => controller.current?.setFriends(props.friends),
    [props.friends],
  );
  useEffect(
    () => controller.current?.setSuspended(props.suspended),
    [props.suspended],
  );
  useEffect(
    () => controller.current?.setFlightIdentity(props.runId, props.intro),
    [props.runId, props.intro],
  );
  useEffect(
    () => controller.current?.setTutorialGuidanceVisible(
      props.tutorialGuidanceVisible,
    ),
    [props.tutorialGuidanceVisible],
  );
  useEffect(() => {
    if (props.world)
      controller.current?.setSnapshot(props.world, props.worldReceivedAt);
  }, [props.world, props.worldReceivedAt]);
  useEffect(
    () => controller.current?.setEventObjective(props.eventObjective),
    [props.eventObjective],
  );
  useEffect(
    () => controller.current?.setMission(props.mission),
    [props.mission],
  );
  useEffect(
    () => controller.current?.setPartyMarker(props.partyMarker),
    [props.partyMarker],
  );
  useEffect(
    () => controller.current?.setLocalPlayerId(props.playerId),
    [props.playerId],
  );
  useEffect(
    () => controller.current?.setControls({ boost: props.boost }),
    [props.boost],
  );
  useEffect(
    () => controller.current?.selectTarget(props.targetId),
    [props.targetId],
  );
  return (
    <div
      className="game-viewport"
      ref={element}
      data-testid="game-viewport"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    />
  );
}
